import { useEffect, useState } from 'react';
import { api, readErr } from '@/lib/api';
import { toast } from '@/lib/toast';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tag, Loader2, Save, Info } from 'lucide-react';

// Round 47 — Admin Pricing & Discount dashboard.
//
// Lets admins set the list price + discount % per (product, currency)
// pair. The wallet deduction path reads from this collection on every
// call, so changes take effect immediately — no deploy needed.
//
// One row per pair (BTR × INR, BTR × USD, Compatibility × INR,
// Compatibility × USD). Future products plug in by adding to
// SUPPORTED_PRODUCTS in services/pricing_overrides.py and they appear
// here automatically.

const PRODUCT_LABEL = {
    btr:           'Birth Time Rectification',
    compatibility: 'Compatibility Reading',
};

const CURRENCY_SYMBOL = { INR: '₹', USD: '$' };

function smallestToMajor(units) {
    return Number((Number(units || 0) / 100).toFixed(2));
}
function majorToSmallest(amount) {
    return Math.round(Number(amount || 0) * 100);
}

export default function AdminPricing() {
    const [rows, setRows]       = useState([]);
    const [loading, setLoading] = useState(true);
    const [drafts, setDrafts]   = useState({});   // keyed by `${product}|${currency}`
    const [saving, setSaving]   = useState(null); // currently-saving key
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get('/admin/pricing');
                if (alive) setRows(data?.rows || []);
            } catch (e) {
                if (alive) toast.error(readErr(e, 'Could not load pricing'));
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [reloadKey]);

    const fetchRows = () => setReloadKey((k) => k + 1);

    const keyOf = (r) => `${r.product}|${r.currency}`;

    const getDraft = (r) => {
        const k = keyOf(r);
        if (drafts[k]) return drafts[k];
        return {
            list_amount:  smallestToMajor(r.list_units),
            discount_pct: r.discount_pct,
        };
    };

    const setDraft = (r, patch) => {
        const k = keyOf(r);
        setDrafts((d) => ({
            ...d,
            [k]: { ...getDraft(r), ...patch },
        }));
    };

    const isDirty = (r) => {
        const k = keyOf(r);
        const d = drafts[k];
        if (!d) return false;
        return (
            majorToSmallest(d.list_amount) !== r.list_units
            || Number(d.discount_pct) !== Number(r.discount_pct)
        );
    };

    const save = async (r) => {
        const k = keyOf(r);
        const d = getDraft(r);
        const list_units = majorToSmallest(d.list_amount);
        const discount_pct = Number(d.discount_pct);
        if (list_units <= 0) {
            toast.error('List price must be greater than zero');
            return;
        }
        if (discount_pct < 0 || discount_pct > 100) {
            toast.error('Discount must be between 0 and 100');
            return;
        }
        setSaving(k);
        try {
            await api.put(`/admin/pricing/${r.product}/${r.currency}`, {
                list_units,
                discount_pct,
            });
            toast.success(`${PRODUCT_LABEL[r.product] || r.product} (${r.currency}) saved`);
            setDrafts((dd) => {
                const next = { ...dd };
                delete next[k];
                return next;
            });
            fetchRows();
        } catch (e) {
            toast.error(readErr(e, 'Could not save pricing'));
        } finally {
            setSaving(null);
        }
    };

    // Group rows by product so the table reads naturally.
    const groups = {};
    for (const r of rows) {
        groups[r.product] = groups[r.product] || [];
        groups[r.product].push(r);
    }

    return (
        <div className="max-w-5xl mx-auto p-6 md:p-10" data-testid="admin-pricing-page">
            <div className="flex items-center gap-3 mb-2">
                <Tag className="text-marigold" size={24} />
                <h1 className="font-serif text-3xl md:text-4xl text-parchment">Pricing & Discounts</h1>
            </div>
            <p className="text-sm text-slate-400 mb-8 max-w-2xl">
                Set the list price and active discount per product, per currency.
                Changes are live immediately — every wallet deduction reads from
                this table.
            </p>

            <Card className="bg-elevated border-marigold/10 rounded-sm p-4 mb-6 flex items-start gap-3 text-sm text-slate-300">
                <Info size={16} className="text-marigold mt-0.5 shrink-0" />
                <div>
                    <strong className="text-parchment">List price</strong> is what the user sees struck-through.{' '}
                    <strong className="text-parchment">Discount %</strong> is applied automatically and shown
                    as a green badge next to the final amount the wallet will be charged.
                </div>
            </Card>

            {loading && (
                <div className="flex items-center gap-2 text-slate-400">
                    <Loader2 className="animate-spin" size={16} /> Loading…
                </div>
            )}

            {!loading && Object.entries(groups).map(([product, productRows]) => (
                <section key={product} className="mb-10" data-testid={`pricing-section-${product}`}>
                    <div className="overline text-marigold mb-3">{PRODUCT_LABEL[product] || product}</div>

                    <Card className="bg-surface border-marigold/15 rounded-sm overflow-hidden">
                        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-marigold/10">
                            {productRows.map((r) => {
                                const k = keyOf(r);
                                const d = getDraft(r);
                                const liveFinal = Math.floor(
                                    majorToSmallest(d.list_amount) * (100 - Number(d.discount_pct || 0)) / 100,
                                );
                                return (
                                    <div key={k} className="p-5 md:p-6" data-testid={`pricing-row-${r.product}-${r.currency}`}>
                                        <div className="flex items-baseline justify-between mb-4">
                                            <div className="font-serif text-xl text-parchment">
                                                {r.currency} · {CURRENCY_SYMBOL[r.currency]}
                                            </div>
                                            {r.has_discount
                                                ? (
                                                    <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 rounded-sm">
                                                        {r.discount_pct}% live
                                                    </Badge>
                                                )
                                                : (
                                                    <Badge className="bg-slate-500/15 text-slate-300 border border-slate-500/30 rounded-sm">
                                                        no discount
                                                    </Badge>
                                                )}
                                        </div>

                                        <div className="grid grid-cols-2 gap-3 mb-4">
                                            <div>
                                                <Label className="text-xs text-slate-400 mb-1">List price ({CURRENCY_SYMBOL[r.currency]})</Label>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    step={r.currency === 'INR' ? '1' : '0.01'}
                                                    value={d.list_amount}
                                                    onChange={(e) => setDraft(r, { list_amount: e.target.value })}
                                                    className="bg-obsidian/60 border-marigold/20 text-parchment rounded-sm"
                                                    data-testid={`pricing-list-${r.product}-${r.currency}`}
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-xs text-slate-400 mb-1">Discount %</Label>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    step="1"
                                                    value={d.discount_pct}
                                                    onChange={(e) => setDraft(r, { discount_pct: e.target.value })}
                                                    className="bg-obsidian/60 border-marigold/20 text-parchment rounded-sm"
                                                    data-testid={`pricing-discount-${r.product}-${r.currency}`}
                                                />
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between text-sm">
                                            <div className="text-slate-400">
                                                User pays{' '}
                                                <span className="text-marigold font-semibold" data-testid={`pricing-final-${r.product}-${r.currency}`}>
                                                    {CURRENCY_SYMBOL[r.currency]}{smallestToMajor(liveFinal)}
                                                </span>
                                            </div>
                                            <Button
                                                onClick={() => save(r)}
                                                disabled={!isDirty(r) || saving === k}
                                                className="bg-marigold hover:bg-marigold-hover text-obsidian rounded-sm h-9 px-4 disabled:opacity-40"
                                                data-testid={`pricing-save-${r.product}-${r.currency}`}
                                            >
                                                {saving === k
                                                    ? <Loader2 className="animate-spin" size={14} />
                                                    : <><Save size={14} className="mr-1.5" /> Save</>}
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </Card>
                </section>
            ))}
        </div>
    );
}
